import { CrossAppMessagingService } from './core/_services/crossApp-messaging.service';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { Component, Inject, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthorizationService } from './core/_services/authorization.service';
import { environment } from 'src/environments/environment';
import { LanguageService } from './core/_services/language.service';
import { TranslateService } from '@ngx-translate/core';
import { WSService } from './core/_services/ws.service';
import { DeviceService } from './core/_services/device.service';
import { SessionService } from './core/_services/session.service';
import { BackNavigationService } from './core/_services/backNavigation.service';
import { DeviceProfileService } from './core/_services/device-profile.service';
import { ClarityService } from './core/_services/clarity.service';
import { OrgThemeService } from './core/_services/orgTheme.service';
import { Settings, SettingsService } from './waiter/_services/settings.service';
import { SHELL_APP_CONFIG } from './core/_interface/shellAppConfig';
import { DeviceSettingsAgentService } from './core/device-settings/device-settings-agent.service';
import { filter, firstValueFrom, pairwise } from 'rxjs';
import { NGXLogger } from 'ngx-logger';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent {
  title = 'Konobar - eMeni.rs';
  environment = environment;
  /** Lokalni prikaz zupčanika posle izlaska iz podešavanja uređaja (~10s); ide u bar kao
   *  [adminUnlocked]. Signal iz admin portala (WS "config") obrađuje sam core device-info-bar. */
  settingsButtonRevealed = false;
  private settingsButtonTimer?: ReturnType<typeof setTimeout>;

  /** Show the shared device bar while nobody is signed in. The waiter login lives on
   *  '/' and '/:companyid' (HomeComponent renders <app-login>), not only on '/login', so the
   *  check is "no user yet" rather than a URL match. Waiting for tokenLoaded keeps the bar
   *  from flashing while a saved session is being restored. Once signed in the same info
   *  lives in the sidebar footer. The '/app' exclusion keeps the bar off the device-settings
   *  page the gear itself opens ('/app/devicesettings', reachable without a user). */
  get onLogin(): boolean {
    return this.auth.tokenLoaded.value && this.auth.currentUser.value == null &&
      !(this.router.url || '').startsWith('/app');
  }
  constructor(public auth: AuthorizationService,
    public router: Router,
    session: SessionService,
    crossAppMessaging: CrossAppMessagingService,
    device: DeviceService,
    backNavigationService: BackNavigationService,
    ws: WSService,
    deviceProfileService: DeviceProfileService,
    settingsService: SettingsService,
    dialog: MatDialog,
    orgTheme: OrgThemeService,
    languageService: LanguageService,
    translate: TranslateService,
    // Injektuje se samo da bi servis nastao — dalje se sam pretplati na `version` frame i
    // primeni serversku odluku o snimanju sesije. Bez injektovanja `providedIn: 'root'`
    // servis nikad ne bi bio instanciran.
    clarity: ClarityService,
    logger: NGXLogger
  ) {
    // Boot watchdog (index.html) ostavlja snimak starta koji je zapeo na splash-u — pošalji ga u
    // log (→ server/Seq), jer na samom uređaju niko ne vidi šta je pošlo naopako. setTimeout:
    // tek posle main.ts emeniBoot.done(), koji u snimak upiše recoveredAfterMs (spor start).
    setTimeout(() => {
      try {
        const raw = localStorage.getItem('emeniBootFailure');
        if (raw) {
          localStorage.removeItem('emeniBootFailure');
          logger.warn('Boot watchdog: prethodni start aplikacije je zapeo', JSON.parse(raw));
        }
      } catch { }
    });

    // Org-level CSS tema: osiguraj `data-app` identifikator (primarno ga postavlja index.html pre-boot).
    orgTheme.ensureAppAttribute('waiter');

    // Izbor jezika (LanguageService pamti izbor po uređaju) primenjuje app, isto kao admin i kiosk.
    // Pretplata ujedno instancira servis, pa se sačuvani jezik primeni odmah po pokretanju.
    languageService.currentLanguage.subscribe(
      lang => {
        if (lang) {
          translate.use(lang);
        }
      }
    );

    // ── Profil podešavanja uređaja ──
    // Koji profil aplikacija koristi zna samo uređaj (lokalno, `appliedProfile`): na startu i na WS
    // signal za taj profil se ponovo učita sa servera; primena je idempotentna po (profileID, version).
    // Profil je default — lokalne izmene pobeđuju, osim kad signal nosi resetOverrides za baš
    // ovaj profil. Native sekcija ide shell-u preko crossAppMessaging (no-op van native app-a).
    const fetchProfile = (reset = false) => {
      const profileID = settingsService.appliedProfile?.profileID;
      if (!profileID) {
        return; // bez profila — nema šta da se učita
      }
      deviceProfileService.loadProfile(profileID).subscribe({
        next: payload => {
          settingsService.applyProfile(payload, reset);
          if (payload?.nativeSettings) {
            crossAppMessaging?.sendRawMessage({
              action: 'applyProfileNativeSection',
              data: JSON.stringify(payload.nativeSettings)
            });
          }
        },
        error: () => { } // offline/bez profila — ostaje poslednje primenjeno stanje
      });
    };

    fetchProfile();

    // Prvi start: čim se aplikacija prvi put poveže na firmu, sama izabere prvi profil firme za svoju
    // aplikaciju — samo jednom; izbor „bez profila" posle toga ostaje.
    deviceProfileService.firstRunProfile('waiter', ws.company$.pipe(filter(company => company.id > 0))).subscribe(payload => {
      settingsService.applyProfile(payload);
      if (payload.nativeSettings) {
        crossAppMessaging?.sendRawMessage({
          action: 'applyProfileNativeSection',
          data: JSON.stringify(payload.nativeSettings)
        });
      }
    });

    ws.observe('deviceProfile.changed').subscribe({
      next: (msg) => {
        const data = msg.data || {};
        if (data.target && data.target !== 'waiter') {
          return;
        }
        // Signal ide svim lokalima organizacije — tiče se samo aplikacije sa tim profilom.
        if (!data.profileID || settingsService.appliedProfile?.profileID !== data.profileID) {
          return;
        }
        fetchProfile(!!data.resetOverrides);
      }
    });

    // ── Podešavanja iz admin portala („⋮" meni uređaja → Podešavanja aplikacije) ──
    // Samo dok je uređaj na vezi. Izmena iz portala ide kroz isti save() kao ekran podešavanja, pa je
    // na uređaju lokalna izmena jača od profila. Registracija je u aplikaciji, ne u waiter modulu,
    // da POS (deli modul) ostane van ovoga dok se ne uvede posebno.
    inject(DeviceSettingsAgentService).register({
      target: 'waiter',
      appVersion: environment.appVersion,
      // Sekcija štampe na ekranu konobara postoji samo kad shell ima štampač — isto pravilo važi i u
      // portalu. Getter: konfiguracija shell-a stiže posle starta aplikacije.
      get nativeApp() {
        return !!SHELL_APP_CONFIG.hasPrinter;
      },
      effective: () => ({ ...settingsService.current }),
      // ⚠ SYNC: isti localStorage ključ kao SettingsService (lokalne izmene postoje samo uz profil).
      overrides: () => {
        if (!settingsService.appliedProfile) {
          return null;
        }
        try {
          return JSON.parse(localStorage.getItem('settingsOverrides') || '{}');
        } catch {
          return {};
        }
      },
      profile: () => {
        const profile = settingsService.appliedProfile;
        return profile
          ? { profileID: profile.profileID, version: profile.version, name: profile.name, settings: profile.settings || {} }
          : null;
      },
      // `current` je deljena referenca po celoj aplikaciji — save() je mutira u mestu.
      apply: values => settingsService.save(Object.assign(new Settings(), settingsService.current, values)),
      clearOverrides: () => settingsService.clearOverrides(),
      // Promena profila iz portala — isto kao izbor na ekranu podešavanja: uređaj učita profil i zapamti ga.
      selectProfile: profileID => firstValueFrom(deviceProfileService.loadProfile(profileID)).then(payload => {
        settingsService.applyProfile(payload);
        if (payload?.nativeSettings) {
          crossAppMessaging?.sendRawMessage({
            action: 'applyProfileNativeSection',
            data: JSON.stringify(payload.nativeSettings)
          });
        }
      }),
    });

    // Browser / Android Back closes only the topmost open layer (dialog or the
    // embedded order screen), not the whole screen. See BackNavigationService.
    backNavigationService.init(dialog, router);

    session.selectedCompany.subscribe(company => {
      var msg = {
        action: "setCompany",
        data: company
      };
      crossAppMessaging?.sendRawMessage(msg);
    });


    session.companySettings.subscribe(settings => {
      var msg = {
        action: "setCompanySettings",
        data: settings
      };
      crossAppMessaging?.sendRawMessage(msg);
    });

    // Tema organizacije: podešavanje `theme` uređaja se rešava iz kataloga tema u company
    // podešavanjima (null = još neučitano → ne diraj); ugrađenu šemu i dalje primenjuje
    // SettingsService. Tenant = companyID; markBootOk → pre-boot watchdog sme da koristi keš pri
    // sledećem paljenju.
    session.companySettings.subscribe(settings => {
      if (settings) {
        orgTheme.applySelection(settingsService.current.theme, settings.organizationCssThemes, session.companyID);
        orgTheme.markBootOk();
      }
    });

    // Odjava BILO KOJIM putem ide kroz signOut() → currentUser pređe u null (i sessionExpired se
    // okida tek posle njega): uređaj može da pređe drugoj organizaciji — ne ostavljaj tuđi
    // brending ni u kešu ni u <head>.
    auth.currentUser.pipe(pairwise()).subscribe(([prev, cur]) => {
      if (prev && !cur) {
        orgTheme.clear();
      }
    });

    auth.sessionExpired.subscribe(
      () => router.navigate(['/login'], { state: { invalidSession: true } })
    )

    // Izlazak sa strane podešavanja uređaja (location.back) ponovo pokaže zupčanik na ~10s, da se
    // podešavanja mogu ispraviti bez ponovnog signala iz admin portala.
    let wasOnSettings = false;
    router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd)).subscribe(e => {
      const url = e.urlAfterRedirects.split('?')[0].split('#')[0];
      const onSettings = url === '/app/devicesettings';
      if (wasOnSettings && !onSettings) {
        this.revealSettingsButton();
      }
      wasOnSettings = onSettings;
    });

    if (environment.nativeApp) {
      inject(CrossAppMessagingService)?.sendRawMessage({
        action: "appLoaded",
        data: { ...environment }
      });

      inject(CrossAppMessagingService)?.sendRawMessage({
        action: "deviceID",
        data: device.deviceID
      });
    };
  }

  private clearSettingsButtonTimer() {
    if (this.settingsButtonTimer) {
      clearTimeout(this.settingsButtonTimer);
      this.settingsButtonTimer = undefined;
    }
  }

  /** Prikaže zupčanik za podešavanja i sam ga ugasi posle ~10s (novi poziv resetuje tajmer). */
  private revealSettingsButton(ms = 10000) {
    this.clearSettingsButtonTimer();
    this.settingsButtonRevealed = true;
    this.settingsButtonTimer = setTimeout(() => {
      this.settingsButtonRevealed = false;
      this.settingsButtonTimer = undefined;
    }, ms);
  }
}
